import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Cloud, ExternalLink } from 'lucide-react';
import { Badge, type BadgeTone } from '../../components/Badge.tsx';
import { Button } from '../../components/Button.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import { CopyButton } from '../../components/CopyButton.tsx';
import { Disclosure } from '../../components/Disclosure.tsx';
import { api, type ServerDomainConnect } from '../onboarding/api.ts';
import { CHECK_INTERVAL_MS } from '../onboarding/provider.ts';
import { useOnboarding } from '../onboarding/store.ts';
import { message, type Letter } from './model.ts';
import styles from './authorization.module.css';

type Published = 'checking' | 'published' | 'missing' | 'replace' | 'unavailable';
type Status = { label: string; tone: BadgeTone; shape: string };

const STATE: Record<Published, Status> = {
  checking: { label: 'Checking your DNS…', tone: 'neutral', shape: '○' },
  published: { label: 'Published', tone: 'positive', shape: '✓' },
  missing: { label: 'Not published yet', tone: 'attention', shape: '△' },
  replace: { label: 'Older letter published', tone: 'attention', shape: '△' },
  unavailable: { label: 'Could not check', tone: 'neutral', shape: '○' },
};
const READ_AS = [
  { label: 'Web page', path: '/verify' },
  { label: 'Markdown', path: '/verify.md' },
  { label: 'JSON', path: '/verify.json' },
];
const CONNECTED: Status = { label: 'Connected', tone: 'positive', shape: '✓' };
const NOT_CONNECTED: Status = { label: 'Not connected yet', tone: 'attention', shape: '△' };

// Reads the customer's own nameserver: is this exact grant published, and is the page connected?
function usePublication(letter: Letter, watch: boolean) {
  const [state, setState] = useState<Published>('checking');
  const [pageConnected, setPageConnected] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const check = useCallback(
    async (quiet = false) => {
      if (!quiet) setState('checking');
      try {
        const read = await api.authorizationPublication(letter.id);
        setState(read.letter);
        setPageConnected(read.pageConnected);
        setDone(read.done);
        setError('');
      } catch (e) {
        setState('unavailable');
        setDone(false);
        setError(message(e));
      }
    },
    [letter.id],
  );
  // Resource loading updates state only after the network request settles.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void check();
  }, [check]);
  // Setup waits on these records, so it keeps looking until they're live.
  useEffect(() => {
    if (!watch || done) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void check(true);
    }, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [watch, done, check]);
  return { state, pageConnected, done, error, check };
}

// One-click publication through the DNS provider (Domain Connect), when it serves this zone and
// has onboarded Traceira's template. Any failure just leaves the manual records.
function useOneClick(letterId: string) {
  const session = useOnboarding((s) => s.server.sessionId) ?? '';
  const [link, setLink] = useState<ServerDomainConnect | null>(null);
  useEffect(() => {
    let live = true;
    api
      .authorizationDomainConnect(letterId, session)
      .then((dc) => {
        if (live) setLink(dc);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [letterId, session]);
  return link?.status === 'available' && link.templateApproved && link.applyUrl
    ? { url: link.applyUrl, provider: link.provider?.name || 'your DNS provider' }
    : null;
}

function lead(state: Published, domain: string) {
  switch (state) {
    case 'published':
      return `Your DNS carries this signed letter. Anyone, and Traceira before every action, verifies it from ${domain}. Delete the record to withdraw.`;
    case 'replace':
      return 'Your DNS carries an earlier letter. Replace that TXT value with the one below; Traceira acts only on the letter you approved last.';
    case 'unavailable':
      return 'Your DNS could not be read just now. Check again in a moment.';
    default:
      return 'Traceira acts only once this signed letter is in your own DNS. Add both records below. DNS changes can take a few minutes to appear.';
  }
}

// The authorization lives on the customer's domain: they publish it, own it and can withdraw it.
export function DomainPublication({
  letter,
  collapsed = false,
  collapseWhenDone = false,
  onDone,
}: {
  letter: Letter;
  collapsed?: boolean;
  /** For the workspace page: once everything is live, these records are nothing left to do. */
  collapseWhenDone?: boolean;
  /** Setup waits on this: every record this letter needs is live. */
  onDone?: (done: boolean) => void;
}) {
  const id = useId();
  const records = letter.records ?? [];
  const { state, pageConnected, done, error, check } = usePublication(letter, Boolean(onDone));
  const oneClick = useOneClick(letter.id);
  useEffect(() => onDone?.(done), [done, onDone]);
  if (records.length === 0) return null;
  const status = STATE[state];
  const domain = letter.terms.domain;
  const txt = records.find((r) => r.type === 'TXT');
  const page = records.find((r) => r.type === 'CNAME');
  // Only a finished read says anything about the page record.
  const pageStatus =
    state === 'checking' || state === 'unavailable'
      ? STATE[state]
      : pageConnected
        ? CONNECTED
        : NOT_CONNECTED;
  const badge = (
    <Badge tone={status.tone} shape={status.shape} data-badge={`publication-${state}`}>
      {status.label}
    </Badge>
  );
  // Outside the panel, so they stay in reach once it collapses.
  const readAs =
    letter.pageUrl && pageConnected ? (
      <div className={styles.actions} role="group" aria-label="Read this authorization">
        {READ_AS.map(({ label, path }) => (
          <a
            key={path}
            className={buttonClass('secondary')}
            href={`${letter.pageUrl}${path}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
            <ExternalLink size={16} aria-hidden="true" />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        ))}
      </div>
    ) : null;
  const body = (
    <>
      <p className={styles.publicationLead}>{lead(state, domain)}</p>
      {error && <p className={styles.note}>{error}</p>}
      <ol className={styles.publishSteps}>
        {txt && (
          <li>
            <p className={styles.stepHead}>
              <strong>Signed letter</strong>
              <span>
                Required · TXT record · Traceira’s signature on these terms · replaces an earlier
                letter
              </span>
              <Badge tone={status.tone} shape={status.shape}>
                {status.label}
              </Badge>
            </p>
            <RecordRow name={txt.name} value={txt.value} />
          </li>
        )}
        {page && (
          <li>
            <p className={styles.stepHead}>
              <strong>Authorization page</strong>
              <span>
                Required · CNAME record, DNS only · serves this authorization on your domain
              </span>
              <Badge tone={pageStatus.tone} shape={pageStatus.shape}>
                {pageStatus.label}
              </Badge>
            </p>
            <RecordRow name={page.name} value={page.value} />
            {letter.pageUrl && !pageConnected && (
              <p className={styles.note}>
                Once this record resolves, you can read it as a web page, Markdown or JSON.
              </p>
            )}
          </li>
        )}
      </ol>
      <div className={styles.actions}>
        {oneClick && state !== 'published' && (
          <a
            className={buttonClass('primary')}
            href={oneClick.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Cloud size={16} aria-hidden="true" />
            Add it in {oneClick.provider}
            <ExternalLink size={16} aria-hidden="true" />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        )}
        <Button onPress={() => void check()} isDisabled={state === 'checking'}>
          {state === 'checking' ? 'Checking…' : 'Check again'}
        </Button>
        <Link className={buttonClass('tertiary')} to="/verify" search={{ domain }}>
          Public verification
        </Link>
      </div>
    </>
  );
  // A management link and a settled workspace page offer this collapsed.
  if (collapsed || (collapseWhenDone && done))
    return (
      <>
        {readAs}
        <Disclosure
          title={
            <span className={styles.publicationTitle}>
              Publish this authorization on your domain {badge}
            </span>
          }
        >
          {body}
        </Disclosure>
      </>
    );
  return (
    <section className={styles.publication} aria-labelledby={id}>
      <div className={styles.header}>
        <div className={styles.intro}>
          <p className={styles.kicker}>On your domain</p>
          <h3 id={id}>Publish this authorization on your domain</h3>
        </div>
        {badge}
      </div>
      {readAs}
      {body}
    </section>
  );
}

function RecordRow({ name, value }: { name: string; value: string }) {
  return (
    <dl className={styles.recordList}>
      <div>
        <dt>Name</dt>
        <dd>
          <code className={styles.recordValue}>{name}</code>
          <CopyButton value={name} what="record name" label="Copy" />
        </dd>
      </div>
      <div>
        <dt>Value</dt>
        <dd>
          <code className={styles.recordValue} title={value}>
            {value}
          </code>
          <CopyButton value={value} what="record value" label="Copy" />
        </dd>
      </div>
    </dl>
  );
}
